import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ArticleCard from "../components/ArticleCard";
import Pagination from "../components/Pagination";
import { getArticles, API } from "../lib/api";
import { ArrowLeft, Folder } from "lucide-react";

const PAGE_SIZE = 20;

export default function CategoryPage() {
  const { category } = useParams();
  const decodedCategory = decodeURIComponent(category);
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10));
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const skip = (page - 1) * PAGE_SIZE;
        const [list, count] = await Promise.all([
          getArticles({
            category: decodedCategory,
            status: "published",
            limit: PAGE_SIZE,
            skip,
          }),
          axios.get(`${API}/articles/count`, {
            params: { category: decodedCategory, status: "published" },
          }),
        ]);
        setArticles(list.data);
        setTotal(count.data?.total ?? list.data.length);
      } catch (error) {
        console.error("Error fetching articles:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [decodedCategory, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const setPage = (newPage) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (newPage <= 1) next.delete("page");
      else next.set("page", String(newPage));
      return next;
    });
  };

  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-[#FF007F] transition-colors mb-8"
          data-testid="back-link"
        >
          <ArrowLeft size={18} />
          Voltar
        </Link>

        {/* Category Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-[#FF007F]/20 flex items-center justify-center">
              <Folder className="w-6 h-6 text-[#FF007F]" />
            </div>
            <div>
              <p className="text-zinc-500 text-sm">Categoria</p>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white" data-testid="category-title">
                {decodedCategory}
              </h1>
            </div>
          </div>
          <p className="text-zinc-400" data-testid="category-count">
            {total === 0
              ? "Nenhuma notícia encontrada"
              : `Mostrando ${rangeStart}–${rangeEnd} de ${total} ${total === 1 ? "notícia" : "notícias"}`}
          </p>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-[#FF007F] border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {/* Articles Grid */}
        {!loading && articles.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article, index) => (
                <div
                  key={article.id}
                  className="animate-fade-in-up opacity-0"
                  style={{ animationDelay: `${index * 0.04}s` }}
                >
                  <ArticleCard article={article} />
                </div>
              ))}
            </div>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
        )}

        {/* Empty State */}
        {!loading && articles.length === 0 && (
          <div className="text-center py-20">
            <Folder className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="font-heading text-2xl font-bold text-white mb-2">
              Nenhuma notícia nesta categoria
            </h3>
            <p className="text-zinc-500 mb-6">
              Em breve teremos novidades em {decodedCategory}!
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF007F] hover:bg-[#FF3399] text-white font-bold rounded-full transition-all"
            >
              Ver todas as notícias
            </Link>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
